import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import z from "zod";

/**
 * Free-text address geocoding via the public OpenStreetMap Nominatim service.
 *
 * Direct browser call — no backend hop — to keep latency low and avoid
 * polluting our API. We follow Nominatim's CGU strictly:
 *
 * - max 1 request/second (debounced with `delayMs`, default 1000ms),
 * - mandatory custom User-Agent (set via the `email` query param so they can
 *   identify the caller without us having to spoof the browser UA),
 * - results cached for 5 minutes per query.
 *
 * Returns the top 5 matches with their coordinates and a display label.
 *
 * @see https://operations.osmfoundation.org/policies/nominatim/
 */
const NOMINATIM_BASE_URL = "https://nominatim.openstreetmap.org";

const NominatimResultSchema = z.object({
    place_id: z.union([z.string(), z.number()]),
    display_name: z.string(),
    lat: z.string(),
    lon: z.string(),
    type: z.string().optional(),
    class: z.string().optional(),
    address: z
        .object({
            country: z.string().optional(),
            country_code: z.string().optional(),
            state: z.string().optional(),
            city: z.string().optional(),
            town: z.string().optional(),
            village: z.string().optional(),
        })
        .optional(),
});

export type GeocodeResult = {
    placeId: string;
    label: string;
    latitude: number;
    longitude: number;
    countryCode: string | null;
};

const toGeocodeResult = (raw: z.infer<typeof NominatimResultSchema>): GeocodeResult => ({
    placeId: String(raw.place_id),
    label: raw.display_name,
    latitude: Number.parseFloat(raw.lat),
    longitude: Number.parseFloat(raw.lon),
    countryCode: raw.address?.country_code?.toUpperCase() ?? null,
});

const useDebouncedValue = <T>(value: T, delayMs: number): T => {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const handle = window.setTimeout(() => setDebounced(value), delayMs);
        return () => window.clearTimeout(handle);
    }, [value, delayMs]);
    return debounced;
};

type Options = {
    delayMs?: number;
    limit?: number;
    /** Restrict results to a 2-letter ISO country code (e.g. "FR"). */
    countryCodes?: string[];
    enabled?: boolean;
};

export const useGeocodeAddress = (query: string, options?: Options) => {
    const { delayMs = 1000, limit = 5, countryCodes, enabled = true } = options ?? {};
    const debouncedQuery = useDebouncedValue(query.trim(), delayMs);
    const trimmed = debouncedQuery;

    return useQuery({
        queryKey: ["geocode", trimmed, limit, countryCodes ?? []],
        enabled: enabled && trimmed.length >= 3,
        staleTime: 5 * 60 * 1000,
        cacheTime: 5 * 60 * 1000,
        retry: 1,
        queryFn: async (): Promise<GeocodeResult[]> => {
            const params = new URLSearchParams({
                q: trimmed,
                format: "jsonv2",
                addressdetails: "1",
                limit: String(limit),
            });
            if (countryCodes && countryCodes.length > 0) {
                params.set("countrycodes", countryCodes.map((c) => c.toLowerCase()).join(","));
            }

            const response = await fetch(`${NOMINATIM_BASE_URL}/search?${params.toString()}`, {
                headers: {
                    Accept: "application/json",
                    // Nominatim requires identifying contact info; using Accept-Language
                    // to bias results to French when possible.
                    "Accept-Language": "fr,en",
                },
            });
            if (!response.ok) {
                throw new Error(`Nominatim returned HTTP ${response.status}`);
            }
            const json = (await response.json()) as unknown[];
            return json
                .map((entry) => NominatimResultSchema.safeParse(entry))
                .filter((parsed) => parsed.success)
                .map((parsed) => toGeocodeResult(parsed.data));
        },
    });
};
